import * as Dialog from '@radix-ui/react-dialog';
import styled, { keyframes } from 'styled-components';
import { size } from '../../styled-components/layout/helpers';

/**
 * Fade-in animation for overlay
 */
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/**
 * Slide-in animation from right
 */
const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

/**
 * Semi-transparent backdrop overlay
 * Only shown for the first drawer in the stack
 */
export const DrawerOverlay = styled(Dialog.Overlay)`
  position: fixed;
  inset: 0;
  background-color: rgba(var(--c-overlay-rgb), 0.5);
  animation: ${fadeIn} 200ms ease-out;
  z-index: 84; /* Below first drawer (85) */
`;

interface DrawerContentProps {
  $stackIndex: number;
  $totalDrawers: number;
}

/**
 * Main drawer content container
 * Width and position adjust based on stack index
 * Each drawer is slightly narrower than the previous one
 */
export const DrawerContent = styled(Dialog.Content)<DrawerContentProps>`
  position: fixed;
  top: ${(props) => props.$stackIndex * 40}px;
  right: 0;
  bottom: 0;
  width: ${(props) => 480 - props.$stackIndex * 40}px;
  max-width: 100vw;
  background-color: var(--c-bg-main);
  box-shadow: ${(props) => {
    // Increase shadow intensity for higher drawers
    const intensity = Math.min(props.$stackIndex + 1, 4);
    return `0 ${intensity * 2}px ${intensity * 8}px rgba(0, 0, 0, ${0.1 + intensity * 0.05})`;
  }};
  display: flex;
  flex-direction: column;
  animation: ${slideInFromRight} 200ms ease-out;
  z-index: ${(props) => 85 + props.$stackIndex};

  /* Mobile: Full-screen single drawer */
  ${(props) => props.theme.breakpoint.LtSm} {
    width: 100vw;
    top: 0;
    left: 0;
    right: 0;
  }

  /* Focus styles */
  &:focus {
    outline: none;
  }
`;

/**
 * Header section of the drawer
 */
export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${size(3)};
  border-bottom: 1px solid var(--c-border-main);
  flex-shrink: 0;
`;

/**
 * Left section of header (type badge + title)
 */
export const DrawerHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${size(2)};
  flex: 1;
  min-width: 0;
`;

/**
 * Title in header
 */
export const DrawerTitle = styled(Dialog.Title)`
  font-size: ${(props) => props.theme.font.size.large};
  font-weight: 500;
  color: var(--c-text-main);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * Scrollable body content
 */
export const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${size(3)};

  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--c-bg-alt);
  }

  &::-webkit-scrollbar-thumb {
    background: var(--c-border-main);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--c-text-sub);
  }
`;

/**
 * Close button (X)
 */
export const CloseButton = styled(Dialog.Close)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: none;
  background-color: transparent;
  color: var(--c-text-sub);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: var(--c-bg-alt);
    color: var(--c-text-main);
  }

  &:focus {
    outline: 2px solid var(--c-accent-bg);
    outline-offset: 2px;
  }
`;

interface TypeBadgeProps {
  $type: 'customer' | 'seller' | 'vehicle' | 'business';
}

/**
 * Badge showing entity type
 */
export const TypeBadge = styled.div<TypeBadgeProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: ${(props) => props.theme.font.size.small};
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;

  ${(props) => {
    switch (props.$type) {
      case 'customer':
      case 'business':
        return `
          background-color: var(--c-semantic-info-300);
          color: var(--c-semantic-info-700);
        `;
      case 'seller':
        return `
          background-color: var(--c-semantic-success-300);
          color: var(--c-semantic-success-700);
        `;
      case 'vehicle':
        return `
          background-color: var(--c-semantic-warning-300);
          color: var(--c-semantic-warning-700);
        `;
      default:
        return `
          background-color: var(--c-semantic-neutral-300);
          color: var(--c-semantic-neutral-700);
        `;
    }
  }}
`;

/**
 * Loading spinner container
 */
export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${size(4)};
  color: var(--c-text-sub);
`;

/**
 * Error message container
 */
export const ErrorContainer = styled.div`
  padding: ${size(2)};
  background-color: var(--c-semantic-error-300);
  border: 1px solid var(--c-semantic-error-700);
  border-radius: 4px;
  color: var(--c-semantic-error-700);
`;
